import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { Form, useFormApi } from "informed";
import { Device, Site, Subject, WebPortalClient, Study, DeviceType } from "webportalclient";
import { ApiClients, ApiPutClientById } from "../../misc/api/AccountServer/AccountApi";
import { QueryCondition, QueryOrderDirection } from "../../misc/api/apiTypes";
import DocumentTitle from "../../misc/components/DocumentTitle";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DeviceForm, DevicesFormModel } from "./DeviceForm";
import { DevicesActionButtons } from "./DevicesActionButtons";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Authorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

declare const rootUrl: string;

interface EditDeviceFormProps {
    id: string;
    study: Study;
    client: WebPortalClient;
    clientDevices: any[];
    deviceTypes: DeviceType[];
    onSetError: React.Dispatch<React.SetStateAction<string>>;
    error: string;
}

export const EditDeviceForm = ({ id, study, client, clientDevices, deviceTypes, onSetError, error }: EditDeviceFormProps) => {
    const formApi = useFormApi<DevicesFormModel>();
    const [sites, setSites] = React.useState<Site[]>([]);
    const [subjects, setSubjects] = React.useState<Subject[]>([]);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [successAlert, setSuccessAlert] = React.useState<boolean>(false);

    React.useEffect(() => {
        const getDropDowns = async () => {
            if (!error) {
                onSetError(undefined);
                const sitesResult = await client.getSites(undefined, `studyId eq ${study.id}`, "number");
                setSites(sitesResult.value);

                const subjectsResult = await client.getSubjects(`id,number`, `site/studyId eq ${study.id}`, "number", "site");
                setSubjects(subjectsResult.value);
            }
        }

        getDropDowns();
    }, [study.id, error]);

    const mapFormToModel = (form: DevicesFormModel): Device => {
        return ({
            deviceType: form.deviceType.id,
            serialNumber: form.serialNumber.serial,
            allocatedSiteId: form.allocatedSite ? form.allocatedSite.id : null,
            allocatedSubjectId: form.allocatedSubject ? form.allocatedSubject.id : null,
        });
    }

    const onSubmit = async () => {
        setSubmitting(true);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return onSetError("Errors exist on the page");
        }

        try {
            const updatedClientModel = {
                ...state.values.serialNumber,
                portal: rootUrl.replace(/\/$/, "")
            }

            const updatedClientResult = await ApiPutClientById(state.values.serialNumber.id, updatedClientModel);

            const model = mapFormToModel(state.values);
            const device = await client.patchDevice(model, id, "");

            if (device) {
                setSuccessAlert(true);
            }
        } catch (ex) {
            onSetError(ex.message);
            setSubmitting(false);
        }

        setSubmitting(false);
    }

    if (sites.length === 0 && subjects.length === 0)
        return <LoadingWithMessage message="Loading Device..." />;

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/deviceadministration`}>Device Administration list</Link>?
            </Alert>
            <Alert show={!!error} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Edit Device - {study.name}</h2>
                </div>
                <div className="col-md-6">
                    <DevicesActionButtons submitting={submitting} onSaveClick={onSubmit} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
            <DeviceForm editMode={true} clientDevices={clientDevices} sites={sites} subjects={subjects} deviceTypes={deviceTypes} />
        </>
    );
}

const mapModelToForm = (model: Device, deviceTypes: DeviceType[], clients: any[]): DevicesFormModel => {
    return ({
        deviceType: deviceTypes.find(dt => dt.id === model.deviceType),
        serialNumber: clients.find(cd => cd.serial === model.serialNumber),
        allocatedSite: model.allocatedSite,
        allocatedSubject: model.allocatedSubject,
    });
}

export const EditDeviceScreen = () => {
    const { id } = useParams<{ id: string }>();
    const client = useWebPortalApiClient();
    const [deviceForm, setDeviceForm] = React.useState<DevicesFormModel>();
    const [clientDevices, setClientDevices] = React.useState([]);
    const [error, setError] = React.useState<string>();
    const [fetching, setFetching] = React.useState<boolean>(true);
    const [currentDeviceStudy, setCurrentDeviceStudy] = React.useState<Study>();
    const [deviceTypes, setDeviceTypes] = React.useState<DeviceType[]>([]);

    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        const getData = async () => {
            setFetching(true);
            const currentDevice = await client.client.getDevice(id, undefined, "allocatedSite,allocatedSubject");
            
            const study = await client.client.getStudy(currentDevice.studyId, "id,name,deviceTypeIds");
            setCurrentDeviceStudy(study);

            const deviceTypesResult = await client.client.getDeviceTypes(undefined, `id in ('${study.deviceTypeIds.join("','")}')`)
            setDeviceTypes(deviceTypesResult.value);

            try {
                const clients = await ApiClients({
                    Filters: [{
                        Member: "serial",
                        Condition: QueryCondition.NotEqual,
                        Values: ["", null]
                    }],
                    Order: {
                        Direction: QueryOrderDirection.Ascending,
                        Member: "serial"
                    }
                });

                const deviceClients = clients.data.filter(c => c.scopes.includes("device"));

                const form = mapModelToForm(currentDevice, deviceTypesResult.value, deviceClients);

                setClientDevices(deviceClients);
                setDeviceForm(form);
            } catch (ex) {
                setError("Unable to connect to Vitalograph Account to query for devices");
            } finally {
                setFetching(false);
            }
        }

        getData();
    }, [id]);

    if (fetching || !currentDeviceStudy)
        return <LoadingWithMessage message="Loading Device..." />;

    return (
        <DocumentTitle title={`Edit Device - ${currentDeviceStudy.name} | Vitalograph Web Portal`}>
            <Authorization currentPermissions={currentUserPermissions} studyId={currentDeviceStudy.id} permissionId={PermissionEnum.ViewDevices}>
                <Form<DevicesFormModel> noValidate initialValues={deviceForm}>
                    <EditDeviceForm id={id} study={currentDeviceStudy} client={client.client} clientDevices={clientDevices} deviceTypes={deviceTypes} error={error} onSetError={setError} />
                </Form>
            </Authorization>
        </DocumentTitle>
    );
}
